import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Search, Filter, ShoppingCart, Plus, Minus, X, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return "https://placehold.co/400x400/e2e8f0/64748b?text=Producto";
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const formatPrice = (price) => {
  return `L ${price.toLocaleString("es-HN", { minimumFractionDigits: 2 })}`;
};

// Cart Context using localStorage
const useCart = () => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("shop_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("shop_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => setCart([]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return { cart, addToCart, removeFromCart, updateQuantity, clearCart, total, itemCount };
};

// Product Card
function ProductCard({ product, onAddToCart }) {
  const image = getImageUrl(product.featured_image_url);
  const hasDiscount = product.compare_price && product.compare_price > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
      data-testid={`product-card-${product.product_id}`}
    >
      <Link to={`/tienda/${product.slug}`} className="block relative aspect-square overflow-hidden">
        <img
          src={image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {hasDiscount && (
          <Badge className="absolute top-3 left-3 bg-red-500 text-white">
            -{discountPercent}%
          </Badge>
        )}
        {product.is_featured && (
          <Badge className="absolute top-3 right-3 bg-amber-500 text-white">
            Destacado
          </Badge>
        )}
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold">Agotado</span>
          </div>
        )}
      </Link>

      <div className="p-4">
        <Link to={`/tienda/${product.slug}`}>
          <h3 className="font-medium text-brand-text group-hover:text-primary transition-colors line-clamp-2 mb-2">
            {product.name}
          </h3>
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <span className="text-lg font-bold text-primary">
              {formatPrice(product.price)}
            </span>
            {hasDiscount && (
              <span className="text-sm text-gray-400 line-through ml-2">
                {formatPrice(product.compare_price)}
              </span>
            )}
          </div>
          
          {product.stock > 0 && (
            <Button
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                onAddToCart(product);
              }}
              className="rounded-full"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Cart Sheet
function CartSheet({ cart, total, itemCount, updateQuantity, removeFromCart, clearCart }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="relative rounded-full" data-testid="cart-button">
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
              {itemCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Carrito ({itemCount} items)
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Tu carrito está vacío</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.product_id} className="flex gap-3 p-3 bg-gray-50 rounded-xl">
                  <img
                    src={getImageUrl(item.featured_image_url)}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-brand-text truncate">{item.name}</h4>
                    <p className="text-primary font-bold">{formatPrice(item.price)}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-6 w-6"
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-red-500"
                    onClick={() => removeFromCart(item.product_id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <SheetFooter className="border-t pt-4">
            <div className="w-full space-y-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
              <Link to="/tienda/checkout" className="block">
                <Button className="w-full rounded-full" size="lg">
                  Realizar Pedido
                </Button>
              </Link>
              <Button variant="outline" className="w-full" onClick={clearCart}>
                Vaciar Carrito
              </Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function TiendaPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const { cart, addToCart, removeFromCart, updateQuantity, clearCart, total, itemCount } = useCart();

  useEffect(() => {
    fetchData();
  }, [categoryFilter, searchQuery]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/public/shop/products`, {
          params: {
            category: categoryFilter || undefined,
            search: searchQuery || undefined,
            limit: 50,
          },
        }),
        axios.get(`${API}/shop/categories`),
      ]);
      setProducts(productsRes.data.items);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const featuredProducts = products.filter((p) => p.is_featured);
  const regularProducts = products.filter((p) => !p.is_featured);

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="tienda"
        title="Tienda Choluteca.net"
        subtitle="Productos promocionales y artículos exclusivos de nuestra región"
        badge="Tienda Oficial"
        badgeIcon={ShoppingBag}
      />

      {/* Filters & Cart */}
      <section className="py-4 bg-white border-b sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-products"
              />
            </div>

            <div className="flex items-center gap-4">
              {/* Category Filter */}
              <Select value={categoryFilter || "all"} onValueChange={(value) => setCategoryFilter(value === "all" ? "" : value)}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Cart */}
              <CartSheet
                cart={cart}
                total={total}
                itemCount={itemCount}
                updateQuantity={updateQuantity}
                removeFromCart={removeFromCart}
                clearCart={clearCart}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-6 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No hay productos</h3>
              <p className="text-gray-500">Pronto agregaremos productos a nuestra tienda</p>
            </div>
          ) : (
            <>
              {/* Featured Products */}
              {featuredProducts.length > 0 && (
                <div className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-brand-text mb-6 flex items-center gap-2">
                    <Tag className="h-6 w-6 text-amber-500" />
                    Productos Destacados
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {featuredProducts.map((product) => (
                      <ProductCard
                        key={product.product_id}
                        product={product}
                        onAddToCart={addToCart}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All Products */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-brand-text mb-6">
                  {categoryFilter
                    ? categories.find((c) => c.id === categoryFilter)?.name || "Productos"
                    : "Todos los Productos"}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {regularProducts.map((product) => (
                    <ProductCard
                      key={product.product_id}
                      product={product}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}

export { useCart };
